<template>
  <div class="ss" data-test="slack-settings">
    <NoticeBox
      v-if="!connected"
      title="Slack isn’t connected"
      link-label="Open Settings"
      :link-href="settingsHref"
    >
      Connect your Slack workspace in Settings to use this action.
    </NoticeBox>

    <div
      class="ss__stack"
      :class="{ 'ss__stack--off': !connected }"
      :aria-disabled="connected ? null : 'true'"
      :inert="connected ? null : ''"
    >
      <SettingsCard title="Message">
        <FieldRow label="Slack channel">
          <WfPicker
            v-model="p.slackChannelObj"
            :options="wfBuilder.slackChannels"
            :searchable="true"
            :show-labels="false"
            :custom-label="wfBuilder.formatSlackChannel"
            track-by="id"
            placeholder="Select channel…"
            @input="onChannel"
          />
        </FieldRow>

        <FieldRow
          label="Message"
          for-id="ss-message"
          note="Available variables:"
          :note-codes="VARIABLES"
        >
          <textarea
            id="ss-message"
            v-model="p.slackMessage"
            class="ss__textarea"
            rows="4"
            :placeholder="wfBuilder.slackMessagePlaceholder"
            data-test="slack-message"
          ></textarea>
        </FieldRow>
      </SettingsCard>
    </div>
  </div>
</template>

<script>
/**
 * Send Slack message (design frame 1n): the channel and the message. The classic fields: `slackChannelObj`
 * (+ `onSlackChannelSelect`, which writes `slackChannelId` / `slackChannelName`) and `slackMessage`. With
 * Slack not connected (`slackConnected`) the amber notice shows (with an "Open Settings" link, in a new tab)
 * and the card is dimmed and inert.
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID.
 */
import FieldRow from './FieldRow.vue';
import NoticeBox from './NoticeBox.vue';
import SettingsCard from './SettingsCard.vue';
import WfPicker from './WfPicker.vue';

// The variables a Slack message can use (the backend fills them at run time).
const VARIABLES = ['{{patient_name}}', '{{patient_phone}}', '{{appointment_date}}', '{{appointment_time}}', '{{provider_name}}'];

export default {
  name: 'SlackSettings',
  components: { FieldRow, NoticeBox, SettingsCard, WfPicker },
  inject: ['wfBuilder'],

  data() {
    return { VARIABLES };
  },

  computed: {
    p() {
      return (this.wfBuilder.selectedNodeData || {}).properties || {};
    },
    connected() {
      return !!this.wfBuilder.slackConnected;
    },
    // Where "Open Settings" goes: the Settings page, as the builder page resolves it. Classic offers no
    // link on this notice, so there is no better destination. '' (no link) when the page has none.
    settingsHref() {
      const href = this.wfBuilder.settingsHref;
      return typeof href === 'string' ? href : '';
    },
  },

  methods: {
    onChannel(val) {
      this.wfBuilder.onSlackChannelSelect(val);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../workflow-tokens';

.ss {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ss__stack {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &--off {
    opacity: 0.5;
    pointer-events: none;
  }
}

.ss__textarea {
  @include wf-textarea;
  min-height: 100px;
}
</style>
